import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

const AdminLogin = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate(from, { replace: true });
  }, [user, loading, from, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    "w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-all";

  return (
    <div
      className="min-h-screen flex items-start md:items-center justify-center px-5 pt-10 md:pt-5 pb-64 md:pb-5 relative overflow-hidden bg-[#05050a]"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 -z-0">
        <div className="absolute top-[20%] left-[20%] w-[500px] h-[500px] rounded-full bg-[#6b21d9]/25 blur-[140px]" />
        <div className="absolute bottom-[10%] right-[15%] w-[500px] h-[500px] rounded-full bg-[#00f0ff]/15 blur-[140px]" />
      </div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="admin-login-back"
          className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white mb-6"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
        <div className="rounded-3xl p-8 md:p-10 bg-[#0a0612]/80 backdrop-blur-md glow-border">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
            SB Media · Admin
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-3">
            Sign in to review{" "}
            <span className="italic font-light text-[#a395c3]">requests.</span>
          </h1>
          <p className="text-[#a395c3] text-sm mt-3">
            Restricted access. Only the SB Media Studio admin can view guest
            submissions.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div className="relative">
              <Mail
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="email"
                autoComplete="username"
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sbmediastudio.com"
                className={input}
              />
            </div>
            <div className="relative">
              <Lock
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="password"
                autoComplete="current-password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={input}
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-[#ff0055] bg-[#ff0055]/10 border border-[#ff0055]/30 rounded-xl px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="group w-full inline-flex items-center justify-center gap-3 pl-6 pr-3 py-3.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold hover:bg-white transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <>
                  Signing in <Loader2 size={16} className="animate-spin" />
                </>
              ) : (
                <>
                  Sign in
                  <span className="w-9 h-9 rounded-full bg-[#05050a] text-[#00f0ff] flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight size={14} />
                  </span>
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
